import type { SkillSource } from "./index"

/**
 * Decision 0139 (memory phase 1, 1.9): how a project checks that its agents remember what was settled. Generic
 * primitives only: a KPI with a recipe and an ordinary recurring item whose worker asks the agent and scores the
 * answers. There is no built-in reviewer and no fixed metric; the project decides what to probe and how often.
 */
export const recallProbe: SkillSource = {
  name: "recall-probe",
  title: "Check what a project's agents remember",
  description:
    "Set up recall checks for a Minga project: a recurring item whose worker asks an agent about things already settled in the records, scores the answers against the records and records a recall KPI. Use it when the person asks whether the agents remember, wants to measure memory, or after an agent forgot something it should have known.",
  body: `# Check what a project's agents remember

A recall check is ordinary Minga work, not a feature: a KPI with a recipe, fed by a recurring item. Each occurrence's
worker picks a few things the records already settle, asks the agent without giving the answer, scores the answers
against the records and records the score. Nothing here is built in; the lead keeps it like any other recurring work.

## Steps

1. Find the project: \`read_project\` with \`project\` = the output of \`git remote get-url origin\`, or the project the
   person names.
2. Agree with the person, in one short message with your defaults: which agent to check (default: the lead), how
   often (default: weekly), how many questions (default: 5), and whether a miss should become a memory fix (default:
   yes).
3. Ask the lead to set it up: \`ask_lead\` with the text under "What to ask the lead", filled in. The lead adds the KPI
   and the recurring item under its mandate, or puts it on one card for the person.
4. Tell the person the refs the lead gives back (the KPI and the item), and that the first numbers come with the first
   occurrence.

## What to ask the lead

> Please set up a recall check for <agent> (<how often>, <n> questions):
>
> - A KPI "Recall: <agent>" (unit %, higher is better, cadence <how often>, role health) whose recipe says: the
>   share of probe questions <agent> answered correctly from the project's records; fed by the recurring item below.
>   A second number is optional: the share it said it did not know (abstained), which is better than a wrong answer.
> - A recurring item "Recall check: <agent>" (<how often>), owned by you, done by a worker, with this description:
>   1. Pick <n> things the records settle, each with its ref, from different kinds: an entry in
>      \`project/decisions.md\`, a decided card, an item closed or a PR merged since the last check, an accepted memory
>      (M-n), a correction a person made in chat (\`look_back\`). Prefer recent ones and ones the agent was told.
>   2. Write each as a plain question whose answer the record holds ("Is #8 merged?", "What did we decide about the
>      paywall tiers, and why?"). Never put the answer or the ref in the question.
>   3. \`ask\` <agent> the questions in one message, asking it to answer from what it knows and to say "don't know"
>      when it is not sure. End the run; its answer wakes the next attempt.
>   4. Score each answer against the record: correct, abstained ("don't know"), or wrong. Post the table (question,
>      expected with its ref, answer, score) as a report output.
>   5. \`record_kpi\` the share correct (and the share abstained, if defined) with the report as evidence.
>   6. For each wrong answer, comment on the item with the ref and what the agent believed, so the agent (or you)
>      fixes the memory: \`remember\` with \`replaces\`, an entry in \`project/decisions.md\`, or a note in its notebook.
> - Checks: a rubric check that every expected answer names its ref and every score follows from it.

## Never

- Build or ask for a new Minga feature for this: the recurring item and the KPI are the whole mechanism.
- Ask about a person's private chat, another person's personal notes, or a private project the agent cannot see.
- Put the expected answers where the agent loads them (its memory, its pages) before it answers.
- Put a secret's value (a token, key or password), customer data or personal data into a question or the report.
`,
}
